import { useState, useEffect } from "react";
import { useChartOfAccounts } from "@/hooks/use-chart-of-accounts";
import { useBusinesses } from "@/hooks/use-businesses";
import { Layout } from "@/components/layout";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectGroup, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { 
  InfoIcon, CheckCircle, AlertTriangle, Wallet, CreditCard, 
  ArrowUp, ArrowDown, Edit, Save, Plus, X
} from "lucide-react";
import { Loader2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/hooks/use-toast";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";

export default function AccountsPage() {
  const { activeBusiness } = useBusinesses();
  const { toast } = useToast();
  const {
    accountTemplates,
    chartOfAccounts,
    accounts,
    accountsByType,
    isLoading,
    generateChartMutation,
    updateAccountMutation,
    createAccountMutation,
    isActualChartOfAccounts
  } = useChartOfAccounts();
  
  // Auto-redirect to direct-accounts page since we don't allow 
  // template changes after initial setup
  useEffect(() => {
    if (activeBusiness?.id) {
      window.location.href = "/direct-accounts?business=" + activeBusiness.id;
    }
  }, [activeBusiness?.id]);
  
  const [selectedBusinessType, setSelectedBusinessType] = useState<string>("");
  const [editingAccount, setEditingAccount] = useState<any>(null);
  const [formState, setFormState] = useState<any>({});
  const [isAddingAccount, setIsAddingAccount] = useState(false);
  const [newAccountForm, setNewAccountForm] = useState({
    code: '',
    name: '',
    type: 'asset',
    subtype: '',
    description: '',
    isActive: true
  });

  // Handle generating chart of accounts
  const handleGenerateChart = () => {
    if (!selectedBusinessType) {
      toast({
        title: "Business Type Required",
        description: "Please select a business type before generating a chart of accounts.",
        variant: "destructive",
      });
      return;
    }
    
    // Detect if this is an LLC business
    const isLlcBusiness = 
      selectedBusinessType === 'llc' || 
      (activeBusiness?.name && activeBusiness.name.toLowerCase().includes('llc'));
    
    // Use LLC specific parameters for LLC businesses
    const effectiveType = isLlcBusiness ? 'llc' : selectedBusinessType;
    
    console.log("Generating chart of accounts:", 
                "Selected type:", selectedBusinessType,
                "Effective type:", effectiveType,
                "Is LLC:", isLlcBusiness,
                "Business name:", activeBusiness?.name);
    
    // For LLC businesses, we use the 'llc' type with forceLlcAccounts flag
    const mutationParam = isLlcBusiness 
      ? { businessType: effectiveType, forceLlcAccounts: true }
      : effectiveType;
    
    generateChartMutation.mutate(mutationParam, {
      onSuccess: (data) => {
        // Check if the operation was restricted - 403 responses from server
        if (data.restricted) {
          console.log("Chart of accounts change restricted:", data.message);
          return; // The error was already handled in the mutation function
        }
        
        console.log("Chart of accounts generated successfully:", data);
        toast({
          title: "Chart of Accounts Generated",
          description: "Your chart of accounts has been successfully created.",
        });
        
        // Set a small timeout to allow the server to finish creating accounts
        setTimeout(() => {
          window.location.href = "/direct-accounts?refresh=" + new Date().getTime();
        }, 500);
      },
      onError: (error: Error) => {
        console.error("Error generating chart of accounts:", error);
        
        // Check if this is a restricted operation error
        if (error.message && error.message.includes("cannot be changed after initial setup")) {
          toast({
            title: "Template Change Restricted",
            description: "Chart of accounts template cannot be changed after initial setup. You can add or edit individual accounts instead.",
            variant: "destructive",
            duration: 6000
          });
          
          // Navigate to direct accounts to view/edit existing accounts
          setTimeout(() => {
            window.location.href = "/direct-accounts?refresh=" + new Date().getTime();
          }, 1000);
          
          return;
        }
        
        // Special handling for LLC businesses if there's an error
        if (isLlcBusiness) {
          console.log("Trying fallback approach for LLC business");
          
          // Try again with sole_proprietorship but force LLC accounts
          generateChartMutation.mutate(
            { businessType: 'sole_proprietorship', forceLlcAccounts: true },
            {
              onSuccess: (data) => {
                if (data.restricted) {
                  console.log("Chart of accounts change restricted:", data.message);
                  return; // The error was already handled in the mutation function
                }
                
                console.log("Chart of accounts generated successfully with fallback:", data);
                toast({
                  title: "Chart of Accounts Generated",
                  description: "Your chart of accounts has been successfully created.",
                });
                
                setTimeout(() => {
                  window.location.href = "/direct-accounts?refresh=" + new Date().getTime();
                }, 500);
              },
              onError: (fallbackError) => {
                console.error("Error with fallback generation:", fallbackError);
                toast({
                  title: "Error",
                  description: "Failed to generate chart of accounts even with fallback method.",
                  variant: "destructive",
                });
              }
            }
          );
          return;
        }
        
        toast({
          title: "Error",
          description: error.message || "Failed to generate chart of accounts.",
          variant: "destructive",
        });
      }
    });
  };

  // Format account types for display
  const formatAccountType = (type: string) => {
    switch (type.toLowerCase()) {
      case "asset":
        return { label: "Assets", icon: <Wallet className="h-5 w-5 mr-2" /> };
      case "liability":
        return { label: "Liabilities", icon: <CreditCard className="h-5 w-5 mr-2" /> };
      case "equity":
        return { label: "Equity", icon: <CheckCircle className="h-5 w-5 mr-2" /> };
      case "income":
        return { label: "Income", icon: <ArrowDown className="h-5 w-5 mr-2" /> };
      case "expense":
        return { label: "Expenses", icon: <ArrowUp className="h-5 w-5 mr-2" /> };
      default:
        return { label: type.charAt(0).toUpperCase() + type.slice(1), icon: <InfoIcon className="h-5 w-5 mr-2" /> };
    }
  };

  const startEditing = (account: any) => {
    setEditingAccount(account);
    setFormState({
      ...account,
    });
  };

  const cancelEditing = () => {
    setEditingAccount(null);
    setFormState({});
  };

  const saveAccount = () => {
    if (editingAccount) {
      updateAccountMutation.mutate({
        id: editingAccount.id,
        chartId: editingAccount.chartId,
        ...formState
      }, {
        onSuccess: () => {
          toast({
            title: "Success",
            description: "Account updated successfully",
          });
          setEditingAccount(null);
        },
        onError: (error: any) => {
          toast({
            title: "Error",
            description: error.message || "Failed to update account",
            variant: "destructive",
          });
        }
      });
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormState({
      ...formState,
      [name]: value
    });
  };

  const handleSwitchChange = (checked: boolean) => {
    setFormState({
      ...formState,
      isActive: checked
    });
  };

  const handleNewAccountChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setNewAccountForm({
      ...newAccountForm,
      [name]: value
    });
  };

  const handleNewAccountSwitchChange = (checked: boolean) => {
    setNewAccountForm({
      ...newAccountForm,
      isActive: checked
    });
  };

  const addAccount = () => {
    if (!chartOfAccounts?.id) {
      toast({
        title: "Error",
        description: "Cannot create account: No chart of accounts found",
        variant: "destructive"
      });
      return;
    }
    
    // Make sure required fields are filled
    if (!newAccountForm.code || !newAccountForm.name || !newAccountForm.type) {
      toast({
        title: "Missing Fields",
        description: "Please provide account code, name, and type",
        variant: "destructive"
      });
      return;
    }
    
    // Create the account
    createAccountMutation.mutate({
      chartId: chartOfAccounts.id,
      ...newAccountForm
    }, {
      onSuccess: () => {
        toast({
          title: "Account Created",
          description: "The new account has been added successfully"
        });
        setIsAddingAccount(false);
        // Reset form
        setNewAccountForm({
          code: '',
          name: '',
          type: 'asset',
          subtype: '',
          description: '',
          isActive: true
        });
      },
      onError: (error: Error) => {
        toast({
          title: "Error",
          description: error.message,
          variant: "destructive",
        });
      }
    });
  };

  return (
    <Layout>
      <div className="p-6">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-3xl font-bold">Chart of Accounts</h1>
          {/* Always show generate button if chart doesn't exist */}
          {(!isActualChartOfAccounts || !chartOfAccounts) && !isLoading ? (
            <Button variant="outline" onClick={handleGenerateChart} disabled={!selectedBusinessType || generateChartMutation.isPending}>
              {generateChartMutation.isPending ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Generating...
                </>
              ) : (
                "Generate Chart of Accounts"
              )}
            </Button>
          ) : isActualChartOfAccounts && (
            <Button variant="outline" onClick={() => window.location.href = "/direct-accounts"}>
              View Advanced Chart of Accounts
            </Button>
          )}
        </div>

        {isLoading ? (
          <div className="flex justify-center items-center min-h-[300px]">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </div>
        ) : !isActualChartOfAccounts ? (
          <Card className="mb-6">
            <CardHeader>
              <CardTitle>Create Chart of Accounts</CardTitle>
              <CardDescription>
                Generate a standard chart of accounts based on your business type
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <Alert>
                  <AlertTriangle className="h-4 w-4" />
                  <AlertTitle>No chart of accounts found</AlertTitle>
                  <AlertDescription>
                    {activeBusiness?.name} doesn't have a chart of accounts yet. Select your business type below to generate one.
                  </AlertDescription>
                </Alert>
                
                <div className="space-y-4">
                  <div>
                    <h3 className="text-sm font-medium mb-2">Business Type</h3>
                    <Select value={selectedBusinessType} onValueChange={setSelectedBusinessType}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select a business type" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectGroup>
                          {/* Show only the dynamic templates if available, otherwise use hardcoded defaults */}
                          {Array.isArray(accountTemplates) && accountTemplates.length > 0 ? 
                            accountTemplates.map((template: any) => (
                              <SelectItem key={template.id} value={template.businessType}>
                                {template.name.replace("COA ", "")}
                              </SelectItem>
                            )) : (
                            <>
                              <SelectItem value="advertising_pr">Advertising or Public Relations</SelectItem>
                              <SelectItem value="agriculture">Agriculture, Ranching, or Farming</SelectItem>
                              <SelectItem value="art_writing">Art, Writing, or Photography</SelectItem>
                              <SelectItem value="automotive">Automotive Sales or Repair</SelectItem>
                              <SelectItem value="religious">Church or Religious Organization</SelectItem>
                              <SelectItem value="construction_general">Construction General Contractor</SelectItem>
                              <SelectItem value="construction_trades">Construction Trades</SelectItem>
                              <SelectItem value="design_engineering">Design, Architecture or Engineering</SelectItem>
                              <SelectItem value="financial_services">Financial Services</SelectItem>
                              <SelectItem value="beauty">Hair Salon, Beauty Salon, or Barber Shop</SelectItem>
                              <SelectItem value="technology">Information Technology</SelectItem>
                              <SelectItem value="insurance">Insurance Agency or Broker</SelectItem>
                              <SelectItem value="landscaping">Lawn Care or Landscaping</SelectItem>
                              <SelectItem value="manufacturing">Manufacturing</SelectItem>
                              <SelectItem value="healthcare">Medical, Dental or Health Services</SelectItem>
                              <SelectItem value="nonprofit">Nonprofit</SelectItem>
                              <SelectItem value="product_based">Product Based Business</SelectItem>
                              <SelectItem value="maintenance">Repair and Maintenance</SelectItem>
                              <SelectItem value="restaurant">Restaurants Caterers and Bars</SelectItem>
                              <SelectItem value="retail">Retail Shop and Online Commerce</SelectItem>
                              <SelectItem value="service_based">Service Based Business</SelectItem>
                              <SelectItem value="transportation">Transportation, Trucking, and Delivery</SelectItem>
                              <SelectItem value="wholesale">Wholesale Distribution and Sales</SelectItem>
                              <SelectItem value="real_estate">Real Estate</SelectItem>
                            </>
                          )}
                        </SelectGroup>
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div>
                    <Button variant="outline" onClick={() => window.location.href = "/direct-accounts"} className="w-full">
                      View Advanced Chart of Accounts
                    </Button>
                  </div>
                </div>
              </div>
            </CardContent>
            <CardFooter>
              <Button 
                onClick={handleGenerateChart}
                disabled={!selectedBusinessType || generateChartMutation.isPending}
                className="w-full"
              >
                {generateChartMutation.isPending ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Generating Chart of Accounts...
                  </>
                ) : (
                  "Generate Chart of Accounts"
                )}
              </Button>
            </CardFooter>
          </Card>
        ) : (
          <>
            <Card className="mb-6">
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle>{chartOfAccounts?.name || 'Chart of Accounts'}</CardTitle>
                  <CardDescription>{chartOfAccounts?.description || 'Your business accounts structure'}</CardDescription>
                </div>
                <div className="flex space-x-2">
                  <Button 
                    variant="outline" 
                    onClick={() => window.location.href = "/direct-accounts"}
                  >
                    View Hierarchy
                  </Button>

                  <Dialog open={isAddingAccount} onOpenChange={setIsAddingAccount}>
                    <DialogTrigger asChild>
                      <Button>
                        <Plus className="h-4 w-4 mr-2" /> Add Account
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="sm:max-w-[525px]">
                      <DialogHeader>
                        <DialogTitle>Add New Account</DialogTitle>
                        <DialogDescription>
                          Create a new account in your chart of accounts.
                        </DialogDescription>
                      </DialogHeader>
                      <div className="grid gap-4 py-4">
                        <div className="grid grid-cols-4 items-center gap-4">
                          <Label htmlFor="new-code" className="text-right">
                            Code
                          </Label>
                          <Input
                            id="new-code"
                            name="code"
                            value={newAccountForm.code}
                            onChange={handleNewAccountChange}
                            className="col-span-3"
                            placeholder="e.g. 1200"
                          />
                        </div>
                        <div className="grid grid-cols-4 items-center gap-4">
                          <Label htmlFor="new-name" className="text-right">
                            Name
                          </Label>
                          <Input
                            id="new-name"
                            name="name"
                            value={newAccountForm.name}
                            onChange={handleNewAccountChange}
                            className="col-span-3"
                            placeholder="Account Name"
                          />
                        </div>
                        <div className="grid grid-cols-4 items-center gap-4">
                          <Label htmlFor="new-type" className="text-right">
                            Type
                          </Label>
                          <Select 
                            name="type" 
                            value={newAccountForm.type} 
                            onValueChange={(value) => setNewAccountForm({...newAccountForm, type: value})}
                          >
                            <SelectTrigger className="col-span-3">
                              <SelectValue placeholder="Select a type" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="asset">Asset</SelectItem>
                              <SelectItem value="liability">Liability</SelectItem>
                              <SelectItem value="equity">Equity</SelectItem>
                              <SelectItem value="income">Income</SelectItem>
                              <SelectItem value="expense">Expense</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="grid grid-cols-4 items-center gap-4">
                          <Label htmlFor="new-subtype" className="text-right">
                            Subtype
                          </Label>
                          <Input
                            id="new-subtype"
                            name="subtype"
                            value={newAccountForm.subtype}
                            onChange={handleNewAccountChange}
                            className="col-span-3"
                            placeholder="e.g. current, fixed, operating"
                          />
                        </div>
                        <div className="grid grid-cols-4 items-center gap-4">
                          <Label htmlFor="new-description" className="text-right">
                            Description
                          </Label>
                          <Textarea
                            id="new-description"
                            name="description"
                            value={newAccountForm.description}
                            onChange={handleNewAccountChange}
                            className="col-span-3"
                            placeholder="Account description"
                          />
                        </div>
                        <div className="grid grid-cols-4 items-center gap-4">
                          <Label htmlFor="new-isActive" className="text-right">
                            Active
                          </Label>
                          <div className="flex items-center space-x-2 col-span-3">
                            <Switch
                              id="new-isActive"
                              checked={newAccountForm.isActive}
                              onCheckedChange={handleNewAccountSwitchChange}
                            />
                            <Label htmlFor="new-isActive">
                              {newAccountForm.isActive ? 'Active' : 'Inactive'}
                            </Label>
                          </div>
                        </div>
                      </div>
                      <div className="flex justify-end gap-3">
                        <Button variant="outline" onClick={() => setIsAddingAccount(false)}>
                          Cancel
                        </Button>
                        <Button onClick={addAccount}>
                          Add Account
                        </Button>
                      </div>
                    </DialogContent>
                  </Dialog>
                </div>
              </CardHeader>
              <CardContent>
                <Tabs defaultValue="asset" className="mt-2">
                  <TabsList className="w-full grid grid-cols-5">
                    <TabsTrigger value="asset">Assets</TabsTrigger>
                    <TabsTrigger value="liability">Liabilities</TabsTrigger>
                    <TabsTrigger value="equity">Equity</TabsTrigger>
                    <TabsTrigger value="income">Income</TabsTrigger>
                    <TabsTrigger value="expense">Expenses</TabsTrigger>
                  </TabsList>

                  {/* Assets Tab */}
                  <TabsContent value="asset" className="mt-4">
                    <div className="rounded-md border">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Code</TableHead>
                            <TableHead>Name</TableHead>
                            <TableHead>Description</TableHead>
                            <TableHead>Status</TableHead>
                            <TableHead className="w-[100px]">Actions</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {accountsByType['asset']?.map((account: any) => (
                            <TableRow key={account.id}>
                              <TableCell className="font-medium">{account.code}</TableCell>
                              <TableCell>{account.name}</TableCell>
                              <TableCell className="max-w-[300px] truncate">{account.description}</TableCell>
                              <TableCell>
                                <Badge variant={account.isActive ? "outline" : "secondary"}>
                                  {account.isActive ? "Active" : "Inactive"}
                                </Badge>
                              </TableCell>
                              <TableCell>
                                <Button 
                                  size="sm" 
                                  variant="ghost" 
                                  onClick={() => startEditing(account)}
                                >
                                  <Edit className="h-4 w-4" />
                                </Button>
                              </TableCell>
                            </TableRow>
                          ))}
                          {(!accountsByType['asset'] || accountsByType['asset'].length === 0) && (
                            <TableRow>
                              <TableCell colSpan={5} className="text-center py-6 text-muted-foreground">
                                No asset accounts found
                              </TableCell>
                            </TableRow>
                          )}
                        </TableBody>
                      </Table>
                    </div>
                  </TabsContent>

                  {/* Liabilities Tab */}
                  <TabsContent value="liability" className="mt-4">
                    <div className="rounded-md border">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Code</TableHead>
                            <TableHead>Name</TableHead>
                            <TableHead>Description</TableHead>
                            <TableHead>Status</TableHead>
                            <TableHead className="w-[100px]">Actions</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {accountsByType['liability']?.map((account: any) => (
                            <TableRow key={account.id}>
                              <TableCell className="font-medium">{account.code}</TableCell>
                              <TableCell>{account.name}</TableCell>
                              <TableCell className="max-w-[300px] truncate">{account.description}</TableCell>
                              <TableCell>
                                <Badge variant={account.isActive ? "outline" : "secondary"}>
                                  {account.isActive ? "Active" : "Inactive"}
                                </Badge>
                              </TableCell>
                              <TableCell>
                                <Button 
                                  size="sm" 
                                  variant="ghost" 
                                  onClick={() => startEditing(account)}
                                >
                                  <Edit className="h-4 w-4" />
                                </Button>
                              </TableCell>
                            </TableRow>
                          ))}
                          {(!accountsByType['liability'] || accountsByType['liability'].length === 0) && (
                            <TableRow>
                              <TableCell colSpan={5} className="text-center py-6 text-muted-foreground">
                                No liability accounts found
                              </TableCell>
                            </TableRow>
                          )}
                        </TableBody>
                      </Table>
                    </div>
                  </TabsContent>

                  {/* Equity Tab */}
                  <TabsContent value="equity" className="mt-4">
                    <div className="rounded-md border">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Code</TableHead>
                            <TableHead>Name</TableHead>
                            <TableHead>Description</TableHead>
                            <TableHead>Status</TableHead>
                            <TableHead className="w-[100px]">Actions</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {accountsByType['equity']?.map((account: any) => (
                            <TableRow key={account.id}>
                              <TableCell className="font-medium">{account.code}</TableCell>
                              <TableCell>{account.name}</TableCell>
                              <TableCell className="max-w-[300px] truncate">{account.description}</TableCell>
                              <TableCell>
                                <Badge variant={account.isActive ? "outline" : "secondary"}>
                                  {account.isActive ? "Active" : "Inactive"}
                                </Badge>
                              </TableCell>
                              <TableCell>
                                <Button 
                                  size="sm" 
                                  variant="ghost" 
                                  onClick={() => startEditing(account)}
                                >
                                  <Edit className="h-4 w-4" />
                                </Button>
                              </TableCell>
                            </TableRow>
                          ))}
                          {(!accountsByType['equity'] || accountsByType['equity'].length === 0) && (
                            <TableRow>
                              <TableCell colSpan={5} className="text-center py-6 text-muted-foreground">
                                No equity accounts found
                              </TableCell>
                            </TableRow>
                          )}
                        </TableBody>
                      </Table>
                    </div>
                  </TabsContent>

                  {/* Income Tab */}
                  <TabsContent value="income" className="mt-4">
                    <div className="rounded-md border">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Code</TableHead>
                            <TableHead>Name</TableHead>
                            <TableHead>Description</TableHead>
                            <TableHead>Status</TableHead>
                            <TableHead className="w-[100px]">Actions</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {accountsByType['income']?.map((account: any) => (
                            <TableRow key={account.id}>
                              <TableCell className="font-medium">{account.code}</TableCell>
                              <TableCell>{account.name}</TableCell>
                              <TableCell className="max-w-[300px] truncate">{account.description}</TableCell>
                              <TableCell>
                                <Badge variant={account.isActive ? "outline" : "secondary"}>
                                  {account.isActive ? "Active" : "Inactive"}
                                </Badge>
                              </TableCell>
                              <TableCell>
                                <Button 
                                  size="sm" 
                                  variant="ghost" 
                                  onClick={() => startEditing(account)}
                                >
                                  <Edit className="h-4 w-4" />
                                </Button>
                              </TableCell>
                            </TableRow>
                          ))}
                          {(!accountsByType['income'] || accountsByType['income'].length === 0) && (
                            <TableRow>
                              <TableCell colSpan={5} className="text-center py-6 text-muted-foreground">
                                No income accounts found
                              </TableCell>
                            </TableRow>
                          )}
                        </TableBody>
                      </Table>
                    </div>
                  </TabsContent>

                  {/* Expenses Tab */}
                  <TabsContent value="expense" className="mt-4">
                    <div className="rounded-md border">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Code</TableHead>
                            <TableHead>Name</TableHead>
                            <TableHead>Description</TableHead>
                            <TableHead>Status</TableHead>
                            <TableHead className="w-[100px]">Actions</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {accountsByType['expense']?.map((account: any) => (
                            <TableRow key={account.id}>
                              <TableCell className="font-medium">{account.code}</TableCell>
                              <TableCell>{account.name}</TableCell>
                              <TableCell className="max-w-[300px] truncate">{account.description}</TableCell>
                              <TableCell>
                                <Badge variant={account.isActive ? "outline" : "secondary"}>
                                  {account.isActive ? "Active" : "Inactive"}
                                </Badge>
                              </TableCell>
                              <TableCell>
                                <Button 
                                  size="sm" 
                                  variant="ghost" 
                                  onClick={() => startEditing(account)}
                                >
                                  <Edit className="h-4 w-4" />
                                </Button>
                              </TableCell>
                            </TableRow>
                          ))}
                          {(!accountsByType['expense'] || accountsByType['expense'].length === 0) && (
                            <TableRow>
                              <TableCell colSpan={5} className="text-center py-6 text-muted-foreground">
                                No expense accounts found
                              </TableCell>
                            </TableRow>
                          )}
                        </TableBody>
                      </Table>
                    </div>
                  </TabsContent>
                </Tabs>
              </CardContent>
            </Card>

            {/* Edit Account Dialog */}
            {editingAccount && (
              <Dialog open={!!editingAccount} onOpenChange={(open) => !open && cancelEditing()}>
                <DialogContent className="sm:max-w-[525px]">
                  <DialogHeader>
                    <DialogTitle>Edit Account</DialogTitle>
                    <DialogDescription>
                      Update account details for {editingAccount.name}
                    </DialogDescription>
                  </DialogHeader>
                  <div className="grid gap-4 py-4">
                    <div className="grid grid-cols-4 items-center gap-4">
                      <Label htmlFor="edit-code" className="text-right">
                        Code
                      </Label>
                      <Input
                        id="edit-code"
                        name="code"
                        value={formState.code || ''}
                        onChange={handleInputChange}
                        className="col-span-3"
                      />
                    </div>
                    <div className="grid grid-cols-4 items-center gap-4">
                      <Label htmlFor="edit-name" className="text-right">
                        Name
                      </Label>
                      <Input
                        id="edit-name"
                        name="name"
                        value={formState.name || ''}
                        onChange={handleInputChange}
                        className="col-span-3"
                      />
                    </div>
                    <div className="grid grid-cols-4 items-center gap-4">
                      <Label htmlFor="edit-description" className="text-right">
                        Description
                      </Label>
                      <Textarea
                        id="edit-description"
                        name="description"
                        value={formState.description || ''}
                        onChange={handleInputChange}
                        className="col-span-3"
                        rows={3}
                      />
                    </div>
                    <div className="grid grid-cols-4 items-center gap-4">
                      <Label htmlFor="edit-isActive" className="text-right">
                        Active
                      </Label>
                      <div className="flex items-center space-x-2 col-span-3">
                        <Switch
                          id="edit-isActive"
                          checked={formState.isActive}
                          onCheckedChange={handleSwitchChange}
                        />
                        <Label htmlFor="edit-isActive">
                          {formState.isActive ? 'Active' : 'Inactive'}
                        </Label>
                      </div>
                    </div>
                  </div>
                  <div className="flex justify-end gap-3">
                    <Button variant="outline" onClick={cancelEditing}>
                      Cancel
                    </Button>
                    <Button onClick={saveAccount}>
                      Save Changes
                    </Button>
                  </div>
                </DialogContent>
              </Dialog>
            )}
          </>
        )}
      </div>
    </Layout>
  );
}